import { MoreVert } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Edit, Trash } from 'react-feather';
import { useParams } from 'react-router-dom';
import AvatarUser from 'src/components/common/AvatarUser';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import { IMenuItem } from 'src/components/Drawer/SidebarMenu';
import DropdownMenu from 'src/components/DropdownMenu';
import {
  useMutateCreateContributionComment,
  useMutateDeleteContributionComment,
  useMutateUpdateContributionComment,
} from 'src/services/mutate/useMutateContribute';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { IContributeNote } from 'src/utils/typings/response/contribute';

const NoteItem = ({
  isFetchingList,
  note,
  refetch,
}: {
  isFetchingList: boolean;
  note: IContributeNote;
  refetch: () => void;
}) => {
  const { id = '' } = useParams();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [comment, setComment] = useState(note?.comment?.comment || '');
  const [openInputComment, setOpenInputComment] = useState(false);
  const { mutateAsync: mutateAsyncCreate, isPending: isPendingCreate } = useMutateCreateContributionComment();
  const { mutateAsync: mutateAsyncUpdate, isPending: isPendingUpdate } = useMutateUpdateContributionComment();
  const { mutateAsync: mutateAsyncDelete, isPending: isPendingDelete } = useMutateDeleteContributionComment();

  useEffect(() => {
    setComment(note?.comment?.comment || '');
  }, [note?.comment?.comment]);

  const isHasComment = useMemo(() => !isEmpty(note.comment), [note.comment]);
  const isDeleteProcess = useMemo(() => isFetchingList || isPendingDelete, [isFetchingList, isPendingDelete]);
  const isCreateUpdateProcess = useMemo(
    () => isFetchingList || isPendingCreate || isPendingUpdate,
    [isFetchingList, isPendingCreate, isPendingUpdate],
  );

  const handleTextInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComment(e?.target.value);
  };

  const submitCreateComment = async () => {
    if (isHasComment) {
      await mutateAsyncUpdate({
        dataUuid: id,
        noteId: note.id,
        commentId: note?.comment?.id,
        body: {
          comment,
        },
      });
    } else {
      await mutateAsyncCreate({
        dataUuid: id,
        noteId: note.id,
        body: {
          comment,
        },
      });
    }

    await refetch();
    handleCloseInputComment();
  };

  const deleteCommentFn = async () => {
    await mutateAsyncDelete({
      dataUuid: id,
      noteId: note.id,
      commentId: note?.comment?.id,
    });
    refetch();
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenInputComment = () => {
    setOpenInputComment(true);
  };

  const handleCloseInputComment = () => {
    setOpenInputComment(false);
  };

  const menuData: IMenuItem[] = [
    {
      text: 'Edit note',
      icon: <IconFeather muiProps={{ sx: { width: '14px', fontSize: 24 } }} icon={<Edit />} />,
      key: 'edit_note',
      onClickFn: () => {
        handleOpenInputComment();
        handleMenuClose();
      },
      disabled: false,
    },
    {
      text: 'Remove',
      onClickFn: async () => {
        await deleteCommentFn();
        handleMenuClose();
      },
      icon: isDeleteProcess ? (
        <CircularProgress size={14} />
      ) : (
        <IconFeather muiProps={{ sx: { width: '14px', height: '14px' } }} icon={<Trash />} />
      ),
      key: 'delete_note',
      disabled: isDeleteProcess,
    },
  ];

  return (
    <Box
      borderRadius={theme.shape.borderRadius / 2}
      p={2}
      bgcolor={theme.palette.background.paper1}
      position="relative"
    >
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1}>
          <AvatarUser alt={note?.admin?.name_display} src={note?.admin?.avatar_url} />
          <Box>
            <Typography variant="subtitle2" align="left">
              {renderTextOrDefault(note.admin?.name_display)}
            </Typography>
            <Box mt={1} />
            <Typography variant="caption" align="left" color="text.secondary" component="p">
              {formatUTCTime(FORMAT_DATE_TIME.FULL_DATE, note.created_at)}
            </Typography>
          </Box>
        </Stack>
        {!isHasComment && !openInputComment && (
          <Box>
            <Button color="primary" size="small" variant="contained" onClick={() => handleOpenInputComment()}>
              Reply
            </Button>
          </Box>
        )}
      </Stack>
      <Box mt={2} />
      <Typography variant="subtitle2" align="left">
        {renderTextOrDefault(note.title)}
      </Typography>
      <Box mt={1} />
      <Typography variant="caption" align="left" color="text.secondary" component="p">
        {renderTextOrDefault(note.note)}
      </Typography>
      {openInputComment && (
        <Box mt={2}>
          <TextField
            id="outlined-multiline-static"
            multiline
            maxRows={4}
            fullWidth
            value={comment}
            onChange={handleTextInputChange}
            disabled={isCreateUpdateProcess}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
            <Button color="primary" size="small" variant="text" onClick={() => handleCloseInputComment()}>
              Cancel
            </Button>
            <ButtonWithLoading
              color="primary"
              size="small"
              variant="contained"
              onClick={submitCreateComment}
              isLoading={isCreateUpdateProcess}
            >
              Reply
            </ButtonWithLoading>
          </Stack>
        </Box>
      )}
      {isHasComment && !openInputComment && (
        <Box
          mt={2}
          borderRadius={theme.shape.borderRadius / 2}
          p={1}
          bgcolor={alpha(theme.palette.primary.main, 0.07)}
          position="relative"
        >
          <IconButton
            aria-label="more"
            id="long-button"
            aria-haspopup="true"
            onClick={handleOpenUserMenu}
            disabled={isDeleteProcess}
            sx={{
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(1),
            }}
          >
            <MoreVert fontSize={'small'} />
          </IconButton>
          <DropdownMenu menuData={menuData} anchorEl={anchorEl} handleMenuClose={handleMenuClose} />
          <Stack direction="row" spacing={1}>
            <AvatarUser alt={note.comment?.user.name_display} src={note.comment?.user.avatar_url} />
            <Box>
              <Typography variant="subtitle2" align="left">
                {renderTextOrDefault(note.comment?.user.name_display)}
              </Typography>
              <Box mt={1} />
              <Typography variant="caption" align="left" color="text.secondary" component="p">
                {formatUTCTime(FORMAT_DATE_TIME.FULL_DATE, note.comment.created_at)}
              </Typography>
            </Box>
          </Stack>
          <Box mt={1} />
          <Typography variant="caption" align="left" color="text.secondary" component="p">
            {renderTextOrDefault(note.comment.comment)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NoteItem;
